/* eslint-disable react/jsx-max-depth */
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import { Button, Divider, FormControl, IconButton, OutlinedInput, Select, Typography } from "@mui/material";
import Slider from "@mui/material/Slider";
import { Stack } from "@mui/system";
import ResetFilterButton from "components/molecules/ResetFilterButton";
import * as React from "react";
import { useRecoilState } from "recoil";
import translations from "translations";
import { rangeSliderFilterSelectorFamily, RangeSliderProps } from "../../State/manageFiltersInUnitsOverview";
import TitleWithFilterCounter from "../TitleWithFilterCounter";
import MaxField from "./MaxField";
import MinField from "./MinField";
import {
  StyledIconButtonWrapper,
  StyledRangeSliderBox,
  StyledRangeSliderRoot,
  StyledResetFilterButtonWrapper,
} from "./styles";
interface RangeSliderFilterProps {
  filterType: keyof RangeSliderProps;
  title: string;
}

const showLabel = [1, 200, 500, 1000, 1200, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 10000];

// Discrete kW steps for the maxActivePower slider.
// The slider works in index space (0–N) so every step is visually equal width.
const MAP_STEPS = [1, 200, 500, 1000, 1200, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 10000];
const stepToKw = (index: number): number => MAP_STEPS[index] ?? MAP_STEPS[MAP_STEPS.length - 1];
const kwToStep = (kw: number): number => {
  const idx = MAP_STEPS.indexOf(kw);
  return idx >= 0 ? idx : MAP_STEPS.length - 1;
};

// Clamp Infinity to the slider max so the thumb renders and fields never show "Infinity".
// maxActivePower uses Infinity as its internal "show all" sentinel but displays 10000.
const clampDisplay = (filterType: keyof RangeSliderProps, value: number[]): number[] =>
  filterType === "maxActivePower" ? [value[0], value[1] === Infinity ? 10000 : value[1]] : value;

const renderFilterValue = (filters: number[], title: string) =>
  filters[0] !== 0 || filters[1] !== Infinity ? (
    <TitleWithFilterCounter selectedLength={1} title={title} />
  ) : (
    <Typography>{title}</Typography>
  );

const getFilterSettings = (filterType: keyof RangeSliderProps, filters: number[]) => {
  switch (filterType) {
    case "maxActivePower":
      return {
        defaultValue: undefined,
        marks: MAP_STEPS.map((kw, i) => ({
          value: i,
          label: showLabel.includes(kw) ? kw.toString() : "",
        })),
        maxmax: MAP_STEPS.length - 1,
        minmin: 0,
        scale: stepToKw,
        renderValue: (title: string) => renderFilterValue(filters, title),
        step: 1,
        unitsForVoltageAndKw: "kW",
        valueLabelDisplay: "auto",
      };
    case "ratedCurrent":
      return {
        defaultValue: 100,
        marks: undefined,
        maxmax: 141,
        minmin: 0,
        renderValue: (title: string) => renderFilterValue(filters, title),
        step: 10,
        unitsForVoltageAndKw: "A",
        valueLabelDisplay: filters[1] === Infinity ? "off" : "auto",
      };
    case "ratedVoltage":
      return {
        defaultValue: 100,
        marks: undefined,
        maxmax: 1001,
        minmin: 0,
        renderValue: (title: string) => renderFilterValue(filters, title),
        step: 100,
        unitsForVoltageAndKw: "V",
        valueLabelDisplay: filters[1] === Infinity ? "off" : "auto",
      };
  }
};

const RangeSliderFilter: React.FC<RangeSliderFilterProps> = ({ filterType, title }) => {
  const [filters, setFilters] = useRecoilState(rangeSliderFilterSelectorFamily(filterType));
  const [draftFilters, setDraftFilters] = React.useState<number[]>(() => clampDisplay(filterType, filters));

  // Keep draft in sync when Recoil state changes externally (e.g. "Reset all filters")
  React.useEffect(() => {
    setDraftFilters(clampDisplay(filterType, filters));
  }, [filterType, filters]);

  const activeFilters = filterType === "maxActivePower" ? draftFilters : filters;
  const activeSetFilters = (val: number[] | ((prev: number[]) => number[])) => {
    if (filterType === "maxActivePower") setDraftFilters(val as number[]);
    else setFilters(val);
  };

  const handleChange = (_event: Event, newValue: number | number[]) => {
    if (typeof newValue === "number") return;
    if (filterType === "maxActivePower") {
      // Slider fires indices → convert back to kW before storing
      setDraftFilters([stepToKw(newValue[0]), stepToKw(newValue[1])]);
      return;
    }
    if (newValue[1] === 141 || newValue[1] === 1001) newValue[1] = Infinity;
    setFilters(newValue as number[]);
  };

  // For maxActivePower the Slider value prop must be in index space (for even spacing)
  const sliderValue =
    filterType === "maxActivePower" ? [kwToStep(activeFilters[0]), kwToStep(activeFilters[1])] : activeFilters;

  const handleApply = () => {
    setFilters(draftFilters);
  };

  const isApplyDisabled = draftFilters[0] === filters[0] && draftFilters[1] === filters[1];

  const { defaultValue, marks, maxmax, minmin, scale, renderValue, step, unitsForVoltageAndKw, valueLabelDisplay } =
    getFilterSettings(filterType, filters);

  return (
    <>
      <FormControl sx={{ m: 1 }} size="small" aria-label={filterType}>
        <Select
          labelId={filterType}
          id={filterType}
          multiple
          displayEmpty
          sx={{ borderRadius: "0px" }}
          value={filters}
          input={<OutlinedInput />}
          renderValue={() => renderValue(title)}
        >
          <StyledRangeSliderRoot filterType={filterType}>
            <StyledRangeSliderBox filterType={filterType}>
              <Stack
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                sx={{ width: filterType !== "maxActivePower" ? "100%" : "50%", borderRadius: "0px" }}
              >
                <MinField
                  filters={activeFilters}
                  valueSetter={activeSetFilters}
                  unitsForVoltageAndKw={unitsForVoltageAndKw}
                />
                <Typography>-</Typography>
                <MaxField
                  filters={activeFilters}
                  valueSetter={activeSetFilters}
                  unitsForVoltageAndKw={unitsForVoltageAndKw}
                />
              </Stack>
              <Slider
                marks={marks}
                defaultValue={defaultValue}
                step={step}
                min={minmin}
                max={maxmax}
                scale={scale}
                sx={{ width: "80%", display: "flex", justifyContent: "center", margin: "auto" }}
                value={sliderValue}
                onChange={handleChange}
                valueLabelDisplay={valueLabelDisplay as "auto" | "off" | "on" | undefined}
                size="small"
              />
            </StyledRangeSliderBox>
            <StyledIconButtonWrapper>
              {filterType !== "maxActivePower" && (
                <IconButton onClick={() => setFilters([filters[0], Infinity])}>
                  <AllInclusiveIcon />
                </IconButton>
              )}
            </StyledIconButtonWrapper>
          </StyledRangeSliderRoot>
          <Divider />
          <StyledResetFilterButtonWrapper
            sx={filterType === "maxActivePower" ? { alignItems: "center", gap: 1, pt: 1 } : undefined}
          >
            <ResetFilterButton
              title={translations.globals.button.reset}
              resetFilters={() => setFilters([0, Infinity])}
            />
            {filterType === "maxActivePower" && (
              <Button
                size="small"
                variant="contained"
                onClick={handleApply}
                disabled={isApplyDisabled}
                sx={{ borderRadius: 0 }}
              >
                {translations.globals.button.apply}
              </Button>
            )}
          </StyledResetFilterButtonWrapper>
        </Select>
      </FormControl>
    </>
  );
};
export default RangeSliderFilter;
